/*
 * "Sign in to your organization" from the AuthNull Authenticator app — the
 * Okta Verify way.
 *
 *   1. The app opens https://<org>/authenticator/signin?state=…&code_challenge=…
 *      in the phone's browser. AuthenticatorSignInPage stores the request in
 *      sessionStorage and starts this org's normal sign-in (/tenant/login →
 *      the self-service console, or the org's SSO).
 *   2. Every sign-in path ends back at /tenant/login with a console token.
 *      login.jsx sees the pending request and, instead of opening the console,
 *      calls finishAppSignIn: the backend mints a short, single-use code bound
 *      to this user and the PKCE challenge.
 *   3. The browser hands back to the app: authnull://signin?code=…&state=…&b=…
 *      The app trades the code with its PKCE verifier, which never left the
 *      phone — so a code caught on its way back is worthless.
 *
 * Backend contract: BACKEND_PLAN_NEXT.md §1 (POST /api/v1/device/appSignIn/code).
 */
import axios from "axios";
import { AUTH_TOKEN, REACT_APP_API } from "../../constants";

const KEY = "authnullAppSignIn";
const LIFETIME_MS = 15 * 60 * 1000;
export const APP_CALLBACK = "authnull://signin";

// RFC 7636: the S256 challenge is 43 base64url characters; state is opaque but
// bounded so nothing odd is stored or echoed back.
const CHALLENGE_RE = /^[A-Za-z0-9_-]{43}$/;
const STATE_RE = /^[A-Za-z0-9_-]{16,128}$/;

/** Validates the app's request; returns an error message, or null when usable. */
export function validateAppSignInRequest(params) {
  if (params.get("client") !== "authnull-authenticator") return "This sign-in link is not from the AuthNull Authenticator app.";
  if (params.get("code_challenge_method") !== "S256") return "This sign-in link uses an unsupported method.";
  if (!CHALLENGE_RE.test(params.get("code_challenge") || "")) return "This sign-in link is incomplete.";
  if (!STATE_RE.test(params.get("state") || "")) return "This sign-in link is incomplete.";
  return null;
}

export function savePendingAppSignIn(params) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({
      state: params.get("state"),
      codeChallenge: params.get("code_challenge"),
      createdAt: Date.now(),
    }));
  } catch (_) {
    /* private mode without storage: the sign-in falls back to the console */
  }
}

/** The request still waiting for a sign-in, or null. Expired ones are dropped. */
export function pendingAppSignIn() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p?.state || !p?.codeChallenge || Date.now() - (p.createdAt || 0) > LIFETIME_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return p;
  } catch (_) {
    return null;
  }
}

export function clearPendingAppSignIn() {
  try { sessionStorage.removeItem(KEY); } catch (_) { /* ignore */ }
}

const callbackUrl = (params) => `${APP_CALLBACK}?${new URLSearchParams(params).toString()}`;

// The API root the app should talk to, absolute. On-prem builds may carry a
// relative or empty REACT_APP_API; the backend's own answer wins when given.
const absoluteApiBase = () => {
  try {
    return new URL(REACT_APP_API || "/", window.location.origin).toString().replace(/\/+$/, "");
  } catch (_) {
    return window.location.origin;
  }
};

/**
 * Exchanges the console session (already in localStorage as `token`) for a
 * one-time code and builds the URL that hands back to the app. Always resolves
 * to a callback URL — on failure it carries `error`, so the app can say what
 * happened instead of waiting forever.
 */
export async function finishAppSignIn(pending) {
  try {
    const { data } = await axios.post(
      `${REACT_APP_API}/api/v1/device/appSignIn/code`,
      { codeChallenge: pending.codeChallenge, codeChallengeMethod: "S256", state: pending.state },
      { withCredentials: true, headers: { "X-Authorization": AUTH_TOKEN() } },
    );
    if (!data?.code) throw new Error("no code");
    return { ok: true, url: callbackUrl({ code: data.code, state: pending.state, b: data.baseUrl || absoluteApiBase() }) };
  } catch (err) {
    const status = err?.response?.status;
    const message =
      status === 404 ? "This server can't add a phone from the browser yet. Use the QR code or activation code from your invite email."
      : status === 401 || status === 403 ? "Your sign-in wasn't accepted. Try again from the app."
      : "Something went wrong while adding your phone. Try again from the app.";
    return {
      ok: false,
      message,
      url: callbackUrl({ error: status === 404 ? "unsupported" : "server_error", error_description: message, state: pending.state }),
    };
  } finally {
    clearPendingAppSignIn();
  }
}
